import React from 'react'
import { useTranslation } from 'react-i18next'
import { AnimatePresence, motion } from 'framer-motion'
import { RecurringIcon } from '@/src/renderer/components/icons'
import type { RecurrenceRule } from '@/src/renderer/lib/db/models'
import { describeRecurrence, recurrenceTone, shortRecurrenceLabel } from '@/src/renderer/lib/recurrence'
import { TaskDropdown } from '../TaskDropdown'
import { RecurrenceDropdownContent } from '../RecurrenceDropdown'
import type { CollapsedDropdowns } from './hooks/useCollapsedDropdowns'
import { WithTooltip } from '@/src/renderer/components/ui/tooltip'

interface CollapsedRecurrenceIconProps {
  isExpanded: boolean
  recurrence: RecurrenceRule | null | undefined
  /** Startdatum der Aufgabe — Saat für die Chips im Wiederkehr-Wähler. */
  seedDate: Date | null | undefined
  dropdowns: CollapsedDropdowns
  /** Setzt die Regel; `null` nimmt sie ab (Papierkorb). */
  onRecurrenceChange: (rule: RecurrenceRule | null) => void
  selectOnDropdownOpen: (open: boolean) => void
  playSoftRemove: () => void
  /** `'label'` nur auf der Seite Wiederkehrend: Zeichen + Kurzform als Pille. */
  display?: 'icon' | 'label'
  /** Nur `display="icon"`: zum Kreispfeil die Kurzform („Jeden 1. Mo“) — allein rechts und mit Platz (#283). */
  withText?: boolean
}

/*
  Auf- und Abtritt über die Breite, damit Ende- und Dauer-Marke daneben nicht springen. `marginRight`
  −4 hebt dabei die 4px Abstand der Zeiten-Gruppe auf, solange die Marke noch schmal ist. Ziel ist
  `auto`: Danach steht die Breite frei, und der Text kommt und geht ohne Breiten-Animation — er wechselt
  beim Größerziehen des Fensters, und dort soll nichts nachschwingen.
*/
const ICON_MOTION = {
  initial: { width: 0, opacity: 0, marginRight: -4 },
  animate: { width: 'auto', opacity: 1, marginRight: 0 },
  exit: {
    width: 0,
    opacity: 0,
    marginRight: -4,
    scale: 0.7,
    transition: { duration: 0.22, ease: [0.4, 0, 0.2, 1], opacity: { duration: 0.08 }, scale: { duration: 0.08 } },
  },
} as const

// Die Pille hat keine feste Breite — ein Tween auf `width: 'auto'` misst am Ende nach und
// springt (siehe Framer-Fall in MEMORY). Sie blendet nur.
const LABEL_MOTION = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0, transition: { duration: 0.08 } },
} as const

/**
 * Die **Wiederholungs-Marke** (CONTEXT.md, „Marken an der Zeile“ und Wiederkehrend): rechts als
 * erstes Glied der Zeiten-Gruppe, links neben Ende- und Dauer-Marke, gebaut wie sie — dieselbe 16px-Fläche
 * fürs Zeichen (der Kreispfeil mit 14px und 1px Rand), Hülle `task-entry__collapsed-badge` (#283, vorher hinter
 * dem Titel). Steht sie dort allein
 * (`withText`), trägt sie die Kurzform („Jeden 1. Mo“); sonst nur das Zeichen, der Satz steht im
 * Tooltip. Klick öffnet den Wiederkehr-Wähler in der Fassung mit Papierkorb; `⇧W` drückt dieselbe
 * Marke (`TaskEntry`, `shortcutMarkTarget`). Sie ist das einzige Zeichen für „kommt wieder" —
 * der Haken bleibt der normale Kreis (geändert 2026-09-13).
 *
 * Auf der Seite **Wiederkehrend** (`display="label"`) ist sie eine Pille mit Kurzform im Ton des
 * Mechanismus — dort ist der Rhythmus die Information.
 *
 * Bleibt montiert und blendet selbst — wie {@link CollapsedRewardIcon}, der Abgangs-Animation wegen.
 * Gemessen wird sie mit der Zeiten-Gruppe; den sichtbaren Text zieht `useCollapsedRowFit` dabei über
 * `data-row-fit="recurrence-text"` wieder ab.
 */
export const CollapsedRecurrenceIcon: React.FC<CollapsedRecurrenceIconProps> = ({
  isExpanded, recurrence, seedDate, dropdowns, onRecurrenceChange, selectOnDropdownOpen, playSoftRemove,
  display = 'icon', withText = false,
}) => {
  const { t, i18n } = useTranslation()
  const label = recurrence
    ? t('recurrence.badgeLabel', { summary: describeRecurrence(recurrence, t, i18n.language) })
    : ''

  return (
    <AnimatePresence initial={false}>
      {!isExpanded && recurrence && (
        <motion.span
          key="recurrence-icon"
          className={`task-entry__recurrence-icon${display === 'label' ? ' task-entry__recurrence-icon--label' : ' task-entry__collapsed-badge'}`}
          {...(display === 'label' ? LABEL_MOTION : ICON_MOTION)}
          transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
          // Ein Klick auf die Marke darf die Zeile darunter nicht auf-/zuklappen.
          onClick={(e) => e.stopPropagation()}
          onDoubleClick={(e) => e.stopPropagation()}
          onMouseEnter={() => dropdowns.switchTo('recurrence')}
        >
          <TaskDropdown
            open={dropdowns.recurrenceOpen}
            onOpenChange={(open) => { dropdowns.setRecurrenceOpen(open); selectOnDropdownOpen(open) }}
            side="bottom"
            align="end"
            alignOffset={16}
            trigger={
              display === 'label' ? (
                <WithTooltip content={label}>
                  <button
                    className={`task-entry__title-icon-btn task-entry__recurrence-label task-entry__recurrence-label--${recurrenceTone(recurrence)}`}
                    type="button"
                    aria-label={label}
                  >
                    <RecurringIcon size={14} />
                    <span className="task-entry__recurrence-label-text">
                      {shortRecurrenceLabel(recurrence, t, i18n.language)}
                    </span>
                  </button>
                </WithTooltip>
              ) : (
                <WithTooltip content={label}>
                  <button className="task-entry__collapsed-badge-trigger" type="button" aria-label={label}>
                    <span className="task-entry__recurrence-mark">
                      <RecurringIcon size={14} />
                      {withText && (
                        <span className="task-entry__recurrence-mark-text" data-row-fit="recurrence-text">
                          {shortRecurrenceLabel(recurrence, t, i18n.language)}
                        </span>
                      )}
                    </span>
                  </button>
                </WithTooltip>
              )
            }
          >
            <RecurrenceDropdownContent
              value={recurrence}
              seedDate={seedDate ?? null}
              onValueChange={(rule) => { onRecurrenceChange(rule); dropdowns.setRecurrenceOpen(false) }}
              onClose={() => dropdowns.setRecurrenceOpen(false)}
              onRemove={() => { playSoftRemove(); onRecurrenceChange(null); dropdowns.setRecurrenceOpen(false) }}
              placement="badge"
            />
          </TaskDropdown>
        </motion.span>
      )}
    </AnimatePresence>
  )
}
